import { create } from 'zustand';

/**
 * 036 (issue 207) — the signal a screen's load effect depends on so that a capture the OUTBOX sent
 * in the background (the beat, `online`, becoming visible, signing in, or a reload finding
 * something already `waiting`) is followed by a re-read, per FR-036: "When the outbox has emptied,
 * every screen showing the affected data MUST re-read from the server." Without this, a background
 * send lands on the server and the page goes on showing the list it loaded before the send — the
 * gap `e2e/review.spec.ts`'s stalled-row capture found (evidence-207-client.md, "Screens re-read
 * after a background send").
 *
 * **This was the whole of `core/reach/` until issue 208, which is where OBSERVED reachability
 * landed instead of here.** `reach.store.ts` holds `reachable`/`unreachable`/`failing` (from real
 * request outcomes, distinct from `core/pwa/connectivity.ts`'s `navigator.onLine`) and calls
 * `advanceResync` — exported from THIS file, unchanged — when the state recovers to `reachable`
 * after not being. This file still knows nothing about reachability itself; it is a sibling that
 * reads this one's own writer, the same way `core/outbox/outbox.store.ts` already did for FR-036's
 * own rule.
 *
 * One counter, not a boolean or a timestamp: a `useEffect` dependency needs a value that CHANGES on
 * every occasion a re-read is due, and two sends close enough together to share a millisecond, or a
 * `waiting`-then-`waiting` pair that never becomes true, would both be silently absorbed by either
 * alternative. Effects re-run on inequality, not on truthiness, so a monotonically increasing count
 * is the one representation an occasion can never fail to register.
 */
const store = create<{ count: number }>()(() => ({ count: 0 }));

/** Read inside a component. Re-renders on every advance. */
export function useResync(): number {
  return store((s) => s.count);
}

/** Read outside React. */
export function resyncNow(): number {
  return store.getState().count;
}

/** Moves the counter. Two callers, each for its own reason: `core/outbox/outbox.store.ts` (an
 *  unwaited answer landed, per review's item B) and `core/reach/reach.store.ts` (the server is
 *  reachable again after not being, issue 208) — neither aware of the other's rule, both simply
 *  asking for a re-read the way `useResync()`'s own consumers already do. */
export function advanceResync(): void {
  store.setState((s) => ({ count: s.count + 1 }));
}

/** Test seam, like every other module-level store here. Production code never calls it. */
export function resetResync(): void {
  store.setState({ count: 0 });
}
